import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { EmptyCard, ErrorCard, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { AdminTable, FilterTabs, SearchBox } from "../components/jamora-admin/ListBits";
import useAdminFetch from "../hooks/useAdminFetch";
import { agentVersionState, canViewPharmacyData, pharmacyPath } from "../lib/jamoraAdmin";
import { formatDateTime } from "../lib/tickets";
import { useAuth } from "../contexts/AuthContext";

const STATE_TABS = [["", "Tous"], ["online", "En ligne"], ["offline", "Hors ligne"]];

// The active print agents of every active pharmacy (the « Monitoring Print Agent » view of the Django admin), read only.
// A workstation shows « En ligne » while a Jamora tab is open on it; a switched-on computer with no browser open shows « Hors ligne ».
export default function JamoraAdminPrinting() {
  const { user } = useAuth();
  const { phase, data, error, reload } = useAdminFetch("/print-agents");
  const releases = useAdminFetch("/print-agent-releases");
  const releaseList = releases.data?.items || [];
  const current = releaseList.find((r) => r.current);
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");
  const canOpenFile = canViewPharmacyData(user);

  const rows = useMemo(() => {
    const term = search.toLowerCase();
    return (data?.items || []).filter((a) => (!state || (state === "online") === a.online)
      && (!term || `${a.pharmacy.name} ${a.label} ${a.hostname}`.toLowerCase().includes(term)));
  }, [data, state, search]);

  if (phase === "loading" || phase === "idle") return <LoadingBlock rows={4} />;
  if (phase === "error") return <ErrorCard message={error} onRetry={reload} />;
  const online = data.items.filter((a) => a.online).length;
  const columns = [
    { id: "pharmacy", label: "Pharmacie", primary: true, cell: (a) => (canOpenFile ? <Link className="text-jamora hover:underline" to={`${pharmacyPath(a.pharmacy.schema)}?tab=impression`}>{a.pharmacy.name}</Link> : a.pharmacy.name) },
    { id: "label", label: "Poste", cell: (a) => <span>{a.label || "—"}{a.hostname && <span className="block text-xs text-slate-500">{a.hostname}</span>}</span> },
    { id: "state", label: "État", cell: (a) => <Pill tone={a.online ? "emerald" : "slate"}>{a.online ? "En ligne" : "Hors ligne"}</Pill> },
    { id: "version", label: "Version", hide: "lg", cell: (a) => <span className="inline-flex flex-wrap items-center gap-1">{a.version || "—"}{agentVersionState(a.version, releaseList) === "outdated" && <Pill tone="amber">Ancienne version</Pill>}</span> },
    { id: "seen", label: "Dernier signal", cell: (a) => (a.last_seen_at ? formatDateTime(a.last_seen_at) : "—") },
    { id: "printers", label: "Imprimantes", hide: "lg", cell: (a) => (a.printers.length ? a.printers.join(", ") : "—") },
  ];

  return (
    <div className="space-y-4" data-testid="admin-printing">
      <PageHeader title="Postes d'impression" description="Les postes équipés du Jamora Print Agent dans les pharmacies actives. Un poste est « en ligne » tant qu'un onglet Jamora y est ouvert."
        actions={<Button variant="outline" size="sm" onClick={reload} data-testid="printing-refresh"><RefreshCw size={14} className="mr-1.5" />Actualiser</Button>} />
      <p className="text-xs text-slate-500" data-testid="printing-updated">
        {online} en ligne sur {data.items.length} · calculé le {formatDateTime(data.generated_at)}{data.cached ? " (gardé 1 minute)" : ""}{data.truncated ? " · liste tronquée" : ""}
      </p>
      {current && (
        <p className="text-xs text-slate-500" data-testid="printing-current-release">
          Version courante de l'agent : <strong className="text-slate-900">{current.version}</strong>{current.created_at ? ` (publiée le ${formatDateTime(current.created_at)})` : ""} · {releaseList.length} version(s) connue(s)
        </p>
      )}
      <FilterTabs label="État du poste" tabs={STATE_TABS} value={state} onChange={setState} testid="printing-state" />
      <SearchBox value={search} onChange={setSearch} placeholder="Pharmacie, poste, nom d'hôte…" testid="printing-search" />
      {!rows.length
        ? <EmptyCard icon={Printer} testid="printing-empty" title={data.items.length ? "Aucun poste ne correspond" : "Aucun poste d'impression"} hint={data.items.length ? "Essayez un autre nom ou un autre état." : "Les postes pairés dans les pharmacies apparaîtront ici."} />
        : <AdminTable columns={columns} rows={rows} rowKey={(a) => `${a.pharmacy.schema}-${a.label}-${a.hostname}`} testid="printing-table" />}
    </div>
  );
}
